/**
 * Strips all non-digit characters from a phone number string.
 */
export const stripPhoneNumber = (phone: string | undefined): string | undefined => {
  if (phone) {
    return phone.replace(/\D+/g, '')
  }
  return undefined
}

/**
 * Returns true if the given string is a valid US phone number.
 * Accepts common formats (with/without country code, dashes, spaces, dots).
 */
export const isPhoneNumberValid = (phone: string | undefined): boolean => {
  if (!phone) return false

  const strippedValue = stripPhoneNumber(phone)
  if (!strippedValue) return false

  return /(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})/.test(
    strippedValue
  )
}

/**
 * Formats a digit string for display as a US phone number: "+1 (999) 999-9999".
 *
 * Display-only — the display adds the `+1 ` prefix; callers that store
 * digits should normalize to the 10-digit national number (see
 * `ControlledPhoneInput`), since `stripPhoneNumber` of the formatted string
 * includes the leading country-code 1.
 * Accepts partial input (formats as far as the digits go) so it can run on
 * every keystroke. A leading country code "1" is normalized into the "+1 "
 * prefix. Replaces the react-native-masked-text dependency (dormant since
 * 2019) previously used for this mask.
 */
export const formatUSPhone = (phone: string | undefined): string => {
  const digits = stripPhoneNumber(phone) ?? ''
  const national = digits.startsWith('1') ? digits.slice(1, 11) : digits.slice(0, 10)
  if (national.length === 0) return ''

  const area = national.slice(0, 3)
  const prefix = national.slice(3, 6)
  const line = national.slice(6, 10)

  let formatted = `+1 (${area}`
  if (national.length > 3) formatted += ')'
  if (prefix) formatted += ` ${prefix}`
  if (line) formatted += `-${line}`
  return formatted
}
